import React, { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BookingForm from './components/BookingForm';
import BookingCalendar from './components/BookingCalendar';
import ConfirmationModal from './components/ConfirmationModal';
import LoadingSpinner from './components/LoadingSpinner';
import { getRestaurantSettings, testApiConnection } from './services/api';
import './App.css';

function App() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState(2);
  const [step, setStep] = useState('calendar'); // 'calendar', 'form', 'confirmation'
  const [bookingData, setBookingData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Erst API-Konnektivität testen
        if (window.rbsAjax?.debug) {
          console.log('Debug-Modus aktiv - führe API-Test durch...');
          await testApiConnection();
        }
        
        const restaurantSettings = await getRestaurantSettings();
        setSettings(restaurantSettings);
      } catch (err) {
        console.error('Error loading settings:', err);
        
        // Detaillierte Fehlermeldung basierend auf dem Fehler
        let errorMessage = 'Fehler beim Laden der Restaurant-Einstellungen';
        
        if (err.message.includes('rbsAjax')) {
          errorMessage = 'WordPress Integration fehlt. Bitte stellen Sie sicher, dass das Plugin korrekt aktiviert ist.';
        } else if (err.message.includes('REST API')) {
          errorMessage = 'REST API nicht verfügbar. Bitte prüfen Sie die WordPress-Konfiguration.';
        } else if (err.message.includes('403') || err.message.includes('Zugriff verweigert')) {
          errorMessage = 'Zugriff auf die API verweigert. Bitte prüfen Sie die Plugin-Berechtigungen.';
        } else if (err.message.includes('404')) {
          errorMessage = 'Plugin-API nicht gefunden. Das Plugin ist möglicherweise nicht richtig installiert.';
        }
        
        setError(errorMessage + ' (Details in der Browser-Konsole)');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleDateTimeSelect = (date, time, guests) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setSelectedGuests(guests);
    setStep('form');
  };

  const handleBookingSubmit = (booking) => {
    setBookingData(booking);
    setStep('confirmation');
  };

  const handleBackToCalendar = () => {
    setStep('calendar');
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleBookingComplete = () => {
    setStep('calendar');
    setSelectedDate(null);
    setSelectedTime(null);
    setBookingData(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Fehler</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100" style={{ padding: '2rem 0', minHeight: 'auto' }}>
        
        {/* Header */}

        {/* Progress Indicator */}
        <div className="max-w-5xl mx-auto bg-white border-2 border-gray-300 rounded-lg py-6 mb-4">
          <div className="flex justify-center">
            <div className="flex items-center">
              {/* Schritt 1 */}
              <div className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                  step === 'calendar' ? 'bg-restaurant-primary text-white border-restaurant-primary' : 
                  step === 'form' || step === 'confirmation' ? 'bg-green-600 text-white border-green-600' : 
                  'bg-white text-gray-800 border-gray-400'
                }`}>
                  <span className="text-lg font-bold">1</span>
                </div>
                <span className={`mt-2 text-sm font-bold ${step === 'calendar' ? 'text-restaurant-primary' : 'text-gray-800'}`}>
                  Datum & Zeit wählen
                </span>
              </div>
              
              {/* Verbindungslinie 1-2 */}
              <div className={`h-1.5 w-24 rounded-full mx-2 ${
                step === 'form' || step === 'confirmation' ? 'bg-green-600' : 'bg-gray-300'
              }`}></div>
              
              {/* Schritt 2 */}
              <div className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                  step === 'form' ? 'bg-restaurant-primary text-white border-restaurant-primary' : 
                  step === 'confirmation' ? 'bg-green-600 text-white border-green-600' : 
                  'bg-white text-gray-800 border-gray-400'
                }`}>
                  <span className="text-lg font-bold">2</span>
                </div>
                <span className={`mt-2 text-sm font-bold ${step === 'form' ? 'text-restaurant-primary' : 'text-gray-800'}`}>
                  Kontaktdaten
                </span>
              </div>
              
              {/* Verbindungslinie 2-3 */}
              <div className={`h-1.5 w-24 rounded-full mx-2 ${
                step === 'confirmation' ? 'bg-green-600' : 'bg-gray-300'
              }`}></div>
              
              {/* Schritt 3 */}
              <div className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                  step === 'confirmation' ? 'bg-restaurant-primary text-white border-restaurant-primary' : 
                  'bg-white text-gray-800 border-gray-400'
                }`}>
                  <span className="text-lg font-bold">3</span>
                </div>
                <span className={`mt-2 text-sm font-bold ${step === 'confirmation' ? 'text-restaurant-primary' : 'text-gray-800'}`}>
                  Bestätigung
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 py-4 bg-white border-4 border-gray-300 rounded-lg">
          {step === 'calendar' && (
            <BookingCalendar
              settings={settings}
              onDateTimeSelect={handleDateTimeSelect}
            />
          )}

          {step === 'form' && (
            <BookingForm
              settings={settings}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              selectedGuests={selectedGuests}
              onSubmit={handleBookingSubmit}
              onBack={handleBackToCalendar}
            />
          )}

          {step === 'confirmation' && bookingData && (
            <ConfirmationModal
              bookingData={bookingData}
              settings={settings}
              onComplete={handleBookingComplete}
            />
          )}
        </div>

        {/* Toast Notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
    </div>
  );
}

export default App; 